'use client';

import { Form, Input } from '@weareconceptstudio/form';
import { useState, useRef, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { Text, useTranslation } from '@weareconceptstudio/core';

import { DEFAULT_ARMENIA_BBOX, buildAddressFromGeocode, getYandexLangCode } from '@/utils/addressFormatting';
import AddressFieldStyle from './style';

const suggestApiKey = process.env.APP_YANDEX_SUGGEST_API_KEY;
const geocodeApiKey = process.env.APP_YANDEX_GEOCODE_API_KEY;

const AddressField = () => {
	const [suggestions, setSuggestions] = useState([]);
	const debounceTimeout = useRef(null);
	const sessionTokenRef = useRef(null);
	const suggestAbortRef = useRef(null);

	const { setValue, clearErrors, control } = useFormContext();
	const { selectedLang, translate } = useTranslation();
	const selectedLangRef = useRef(selectedLang);
	selectedLangRef.current = selectedLang;

	// Keep the latest resolved coordinates available to the validator below.
	const geoCoords = useWatch({ control, name: 'geo_coords' });
	const geoCoordsRef = useRef(geoCoords);
	geoCoordsRef.current = geoCoords;

	// A manually typed address is only valid once it has been resolved to
	// coordinates (i.e. the user picked one of the suggestions). Free text that
	// was never geocoded cannot resolve a delivery zone, so block the submit.
	const addressRules = useMemo(
		() => [
			{
				validator: (value) => {
					if (!value || !value.trim()) {
						return true;
					}

					const coords = geoCoordsRef.current;
					const hasCoords = coords && coords.lat != null && coords.lon != null;

					return hasCoords ? true : translate('addressSelectFromList');
				},
			},
		],
		[translate]
	);

	const getSessionToken = () => {
		if (!sessionTokenRef.current) sessionTokenRef.current = uuidv4();
		return sessionTokenRef.current;
	};

	const resetSessionToken = () => {
		sessionTokenRef.current = null;
	};

	const getAddress = async (addressValue) => {
		if (!addressValue) return;

		if (suggestAbortRef.current) suggestAbortRef.current.abort();
		const controller = new AbortController();
		suggestAbortRef.current = controller;

		try {
			const sessionToken = getSessionToken();
			const yandexLang = getYandexLangCode(selectedLangRef.current);
			const res = await fetch(
				`https://suggest-maps.yandex.ru/v1/suggest?apikey=${suggestApiKey}&text=${encodeURIComponent(addressValue)}&lang=${yandexLang}&print_address=1&attrs=uri&bbox=${DEFAULT_ARMENIA_BBOX}&sessiontoken=${sessionToken}&types=street,house&strict_bounds=1`,
				{ signal: controller.signal }
			);
			const data = await res.json();
			setSuggestions(data?.results || []);
		} catch (err) {
			if (err.name !== 'AbortError') console.error('Error fetching address suggestions:', err);
		}
	};

	const getCoordinates = async ({ uri, geocode }) => {
		const yandexLang = getYandexLangCode(selectedLangRef.current);
		const params = new URLSearchParams({
			apikey: geocodeApiKey,
			geocode,
			format: 'json',
			lang: yandexLang,
			bbox: DEFAULT_ARMENIA_BBOX,
			results: '1',
		});
		if (uri) params.set('uri', uri);

		const res = await fetch(`https://geocode-maps.yandex.ru/v1/?${params.toString()}`);
		const data = await res.json();
		const geoObj = data.response.GeoObjectCollection.featureMember[0].GeoObject;
		const [lon, lat] = geoObj.Point.pos.split(' ');
		return {
			lat: parseFloat(lat),
			lon: parseFloat(lon),
			geoObj,
		};
	};

	const onInputChange = (e) => {
		const val = e.target.value;
		clearErrors('address');
		// Editing the text invalidates any previously resolved coordinates, so the
		// user must re-select a suggestion before the address is considered valid.
		setValue('geo_coords', null);
		if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
		debounceTimeout.current = setTimeout(() => getAddress(val), 300);
	};

	const onSelectSuggestion = async (suggestion) => {
		setSuggestions([]);

		const fallbackAddress = suggestion.title.text || suggestion.address?.formatted_address || '';
		setValue('address', fallbackAddress);

		try {
			const result = await getCoordinates({
				uri: suggestion.uri,
				geocode: suggestion.address.formatted_address,
			});

			if (result) {
				setValue('geo_coords', { lat: result.lat, lon: result.lon });
				setValue('address', buildAddressFromGeocode(result.geoObj) || fallbackAddress);
				// Address resolved to coordinates — clear the "select from list" error.
				// Delivery-zone validation is then handled by the map field effect.
				clearErrors('address');
			}
		} catch {
			// geocode failed — address text is already set, coords stay null
		}

		resetSessionToken();
	};

	useEffect(() => {
		const handleClickOutside = (event) => {
			const container = document.querySelector('.suggestion-address');
			if (container && !container.contains(event.target)) {
				setSuggestions([]);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	return (
		<>
			<Form.Item
				className='full-width'
				label='address'
				name='address'
				rules={addressRules}>
				<AddressFieldStyle className='suggestion-address'>
					<Input
						placeholder='addressPlaceholder'
						onChange={onInputChange}
						name='address'
					/>
					{suggestions.length > 0 && (
						<div className='suggested-container'>
							<div className='suggested-menu'>
								{suggestions.map((sug, index) => (
									<div
										className='suggested-menu-item'
										key={index}
										onMouseDown={(e) => {
											e.preventDefault();
											onSelectSuggestion(sug);
										}}>
										<Text className={'p3 font-montserrat-arm-medium background-color'}>
											{sug.title.text}
										</Text>
										{sug.address?.formatted_address ? (
											<Text className='suggested-menu-subtitle p4 font-montserrat-arm-regular'>
												{sug.address.formatted_address}
											</Text>
										) : null}
									</div>
								))}
							</div>
						</div>
					)}
				</AddressFieldStyle>
			</Form.Item>
			<Form.Item
				name='geo_coords'
				required={false}
				noStyle={true}>
				<Input type='hidden' />
			</Form.Item>
		</>
	);
};

export default AddressField;
